import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Message, Modal } from '@arco-design/web-react';
import defaultLocale from '../locale';
import { ErrorCode } from './errno';

export enum ContentType {
  XHTML = 'application/xhtml+xml', // XHTML格式
  XML = 'application/xml', //  XML数据格式
  ATOM_XML = 'application/atom+xml', // Atom XML聚合格式
  JSON = 'application/json', //  JSON数据格式
  PDF = 'application/pdf', // pdf格式
  WORD = 'application/msword;charset=utf-8', //  Word文档格式
  EXCEL = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8', //  excel文档格式
  STREAM = 'application/octet-stream', //  二进制流数据（如常见的文件下载）
  FORM_URLENCODED = 'application/x-www-form-urlencoded', //  <form encType=””中默认的encType，form表单数据被编码为key/
  FORM_DATA = 'multipart/form-data', //  需要在表单中进行文件上传时，就需要使用该格式
  ZIP = 'application/zip',
}

const locale = (key: string): string => {
  const lang = localStorage.getItem('arco-lang') || 'en-US';
  return defaultLocale?.[lang]?.[key] || key;
};

const SKIP_BEFORE_UNLOAD_ON_LOGIN_REDIRECT_KEY = 'SKIP_BEFORE_UNLOAD_ON_LOGIN_REDIRECT';
const LOGIN_REDIRECT_DELAY_MS = 1200;
let isRedirectingToLogin = false;

export function getDeviceId() {
  // 定义存储在浏览器里的 key 名称
  const DEVICE_ID_KEY = 'my_product_device_id';
  // 1. 【读取】：先去浏览器的 LocalStorage 翻翻看有没有旧的
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  // 2. 【判断】：如果有，直接返回（说明这台电脑之前来过）
  if (deviceId) {
    return deviceId;
  }

  // 3. 【生成】：如果没有（说明是新设备，或者用户清空了缓存），生成一个新的 UUID
  // crypto.randomUUID() 是现代浏览器原生支持的方法
  deviceId = crypto.randomUUID();

  // 4. 【写入】：把新生成的 ID 存进 LocalStorage，下次来就有了
  localStorage.setItem(DEVICE_ID_KEY, deviceId);

  return deviceId;
}

// 网络请求的请求头 拦截处理公共逻辑
export const interceptorRequestConfig = (config: AxiosRequestConfig | any) => {
  //
  const Lang = localStorage.getItem('arco-lang') || 'en-US';
  config.headers = {
    ...config.headers,
    'X-App-Id': 2,
    'Content-Type': ContentType.JSON,
    'X-Acc-Lang': Lang,
    'X-Device-Id': getDeviceId(),
  };

  config.params = {
    ...config.params,
    app_id: 2,
  };

  return config;
};

export const clearBrowserCookie = () => {
  // 清除 Cookies
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

  console.log('Local Storage, Session Storage, and Cookies have been cleared.');
};

// 是否需要跳转到登录页面
const redirectToLogin = (message = '') => {
  if (window.location.pathname.includes('/login')) {
    return;
  }

  if (isRedirectingToLogin) {
    return;
  }

  isRedirectingToLogin = true;

  // 清除所有浏览器缓存

  clearBrowserCookie();
  localStorage.removeItem('userInfo');

  if (message) {
    Message.error(message);
    sessionStorage.setItem(SKIP_BEFORE_UNLOAD_ON_LOGIN_REDIRECT_KEY, '1');
    window.setTimeout(() => {
      window.location.replace('/login');
    }, LOGIN_REDIRECT_DELAY_MS);
    return;
  }

  // 重定向到登录页面
  sessionStorage.setItem(SKIP_BEFORE_UNLOAD_ON_LOGIN_REDIRECT_KEY, '1');
  window.location.replace('/login');
};

// 记录是否已经弹出过鉴权失败的 Modal
let authFailedModalShown = false;
const showPasswordExpiredModal = () => {
  authFailedModalShown = true;
  Modal.info({
    autoFocus: false,
    closable: true,
    title: locale('exit_modal_password_expired_content'),
    okText: locale('exit_modal_password_expired_button'),
    onOk: () => {
      authFailedModalShown = false;
      window.open('/pwd/reset');
    },
    afterClose: () => {
      authFailedModalShown = false;
    },
  });
};

// 网络请求的响应体 拦截处理公共逻辑
export const interceptorResponseConfig = (response: AxiosResponse | any) => {
  const { data } = response;

  const errorCode = data.code;

  // 成功响应时直接返回数据
  if (errorCode === ErrorCode.SUCCESS) {
    return data;
  }

  // 根据不同的错误代码处理响应
  switch (errorCode) {
    case ErrorCode.NOT_LOGIN:
      // 未鉴权
      redirectToLogin(locale('login_status_expired_relogin'));
      break;

    case ErrorCode.H_ERR_USER_BLOCKED:
      // 账户被禁用
      redirectToLogin(locale('login_user_blocked'));
      break;

    case ErrorCode.H_ERR_USER_CHANGE_CYCLE:
      // 密码过期
      localStorage.setItem('PASSWORD_EXPIRED', '1');
      if (!authFailedModalShown && !['/pwd', '/login'].some(path => window.location.pathname.includes(path))) {
        showPasswordExpiredModal();
      }
      break;

    default:
      // 处理未知错误代码
      console.warn(`Unhandled error code: ${errorCode}`);
      break;
  }

  return Promise.reject(data);
};

// 网络请求的响应中 拦截处理失败的case
export const interceptorResponseError = (error: AxiosError) => {
  if (error?.response) {
    console.log(`错误信息:${error.response.status}`);
  }
  return Promise.reject(error);
};
// 网络请求的请求头 拦截处理失败逻辑
export const interceptorRequestError = (error: AxiosError) => {
  return Promise.reject(error);
};
